import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useSession } from '@/shared/hooks/use-session';
import { APP_ROUTES } from '@/api/constants/app.url';
import type { DashboardRole } from '@/shared/types/session';

/**
 * Gate component. Wraps a `<Route element={<ProtectedRoute />}>` so every
 * child route is only reachable to authenticated users. Optionally
 * enforces a role (RBAC). On reject, redirects to /sign-in and stashes the
 * attempted URL in `state.from` so the post-sign-in landing can bounce
 * the user back where they were headed.
 *
 * Why a component (not a HOC):
 *   - Plays naturally with React Router v6's element-based config.
 *   - Read-once on render (cookie lookup is cheap), no provider wiring.
 */
interface Props {
    /** If set, the session's role must be one of these to access. */
    roles?: DashboardRole[];
}

export function ProtectedRoute({ roles }: Props) {
    const { session, isLoading } = useSession();
    const location = useLocation();

    // The session hook resolves synchronously today, but keep the loading
    // gate so a future async auth provider (Clerk) drops in cleanly.
    if (isLoading) return null;

    if (!session) {
        return <Navigate to={APP_ROUTES.SIGN_IN} replace state={{ from: location }} />;
    }

    if (roles && !roles.includes(session.role)) {
        // No "403" page yet; redirect home. When we add one, swap this Navigate.
        return <Navigate to={APP_ROUTES.HOME} replace />;
    }

    return <Outlet />;
}
